import type { PayloadAction } from '@reduxjs/toolkit';
import type { ApiActivity, ApiActivityPlan, ApiAttachment } from '../../types/llinkidApiTypes';
import type { ApiMeta, HrefObject } from '../../types/genericApiTypes';
import type { ModalStatus } from '../../types/modalTypes';
import type { CurriculumParams } from '../../types/selectorTypes';

// ==================== Shared Calendar Type Definitions ====================

// ISO 8601 date-time string (e.g., "2025-11-13T10:30:00.000Z")
export type ISODateString =
  `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

export type ColorPair = {
  background: string;
  text: string;
};

export type Period = {
  startDate: ISODateString;
  endDate: ISODateString;
  dtStartDate: Date;
  dtEndDate: Date;
  interval: {
    start: Date;
    end: Date;
  };
  unixStartDate: number;
  unixEndDate: number;
};

export type SimplePeriod = {
  startDate: ISODateString;
  endDate: ISODateString;
};

export type ActivityWithDates = ApiActivity & {
  period: Period;
  hashString?: string;
  position?: number;
  $$color?: ColorPair;
  $$class?: ClassWithColor;
  mergedItems?: ActivityWithDates[];
};

export type ActivityWithDatesAndChildren = ActivityWithDates & {
  children: ActivityWithDates[];
};

export type ActivityWithChildren = ApiActivity & {
  children?: ApiActivity[];
};

export type ClassWithColor = {
  href: string;
  $$displayName: string;
  $$meta: ApiMeta | { permalink: string };
  key: string;
  endDate?: ISODateString | undefined;
  color: ColorPair;
  selected?: boolean;
};

export type Week = {
  key: string;
  description: string | null;
  period: SimplePeriod;
  goals: HrefObject[];
  attachments: any[];
};

export type Teacher = {
  $$meta: ApiMeta | { permalink: string };
  $$displayName: string;
  [key: string]: unknown;
};

export type DateRange = {
  start: ISODateString | Date;
  end: ISODateString | Date;
};

export type DateOption = {
  value: string;
  name: string;
};

export type Goal = {
  href: string;
  $$meta: ApiMeta;
  [key: string]: unknown;
};

export type Curriculum = {
  name: string;
  versionNumber?: string;
  [key: string]: unknown;
};

export type PlanGroup = {
  type: string;
  title?: string;
  creators?: HrefObject[];
};

export type ActivityPlanWithExtras = Omit<
  ApiActivityPlan,
  'activities' | 'class' | 'activityplangroup'
> & {
  activities: ActivityWithDates[];
  class: ClassWithColor;
  classes?: string[];
  mergedFrom?: string[];
};

export type ActivityGroup = {
  id: string;
  $$classesName: string;
  title: string;
  description: string;
  color: ColorPair;
  attachments: ApiAttachment[];
  goals: HrefObject[];
  position: number;
  arrowState: 'START' | 'END' | 'MIDDLE' | 'SINGLE';
  editable: boolean;
  dragging: boolean;
  activities_del: ActivityWithDates[];
  parentActivities: string[];
  empty?: boolean;
};

export type ActivityPlanForWeek = {
  activityGroups: ActivityGroup[];
  classes?: string[];
  canMoveBack?: boolean;
};

export type CalendarWeek = {
  id: number;
  dateRange: {
    start: Date;
    end: Date;
  };
  isFirstWeek: boolean;
  holidays: null;
  startDate: Date;
  endDate: Date;
  activityPlans: ActivityPlanForWeek[];
  isHoliday?: boolean;
  holidayName?: string;
};

export type CalendarVM = {
  weeks: CalendarWeek[];
  occurencesPerGoal: Map<string, number>;
};

export type ColorMap = Record<string, ColorPair>;

// ==================== State and Action Types ====================

export type CreateActivityModalData = {
  createForClasses: string[];
  week: {
    startDate: ISODateString;
    endDate: ISODateString;
  };
  isCreate: true;
};

export type EditActivityModalData = {
  createForClasses: string[];
  isCreate: false;
  parentHrefs: string[];
  key: string;
};

// Type guard to check if data is of type CreateActivityModalData
// const isCreateActivityModalData = (
//   data: CreateActivityModalData | EditActivityModalData | null
// ): data is CreateActivityModalData => {
//   return data !== null && data.isCreate === true;
// };

// Type guard to check if data is of type EditActivityModalData
export const isEditActivityModalData = (
  data: CreateActivityModalData | EditActivityModalData | null,
): data is EditActivityModalData => {
  return data !== null && data.isCreate === false;
};

export type DuplicateActivityClass = {
  href: string;
  $$displayName: string;
  $$meta: { permalink: string };
  color: { background: string };
  selected: boolean;
};

export type DuplicateActivityModalData = {
  model: {
    title: string;
    goals: HrefObject[];
    weeks: Week[];
  };
  classes: DuplicateActivityClass[];
};

export type CalendarState = Readonly<{
  calendarKeys: string[];
  merge: boolean;
  unselectedClasses: string[];
  selectionMode: boolean;
  selectionOptions: {
    selectionType: string | null;
    confirmLabel: string | null;
    cancelLabel: string | null;
    scrollPosition: string | null;
  };
  editActivityModal: {
    status: ModalStatus;
    data: CreateActivityModalData | EditActivityModalData | null;
  };
  duplicateActivityModal: {
    status: ModalStatus;
    data: DuplicateActivityModalData | null;
  };
  scrollToDate: string | null;
}>;
export type EditActivityModalOpenPayload = PayloadAction<
  CreateActivityModalData | EditActivityModalData
>;
export type MoveWeekPayload = PayloadAction<{ startDate: number; type: 'ADD' | 'SUBTRACT' }>;
export type DeleteActivityPayload = PayloadAction<{ parentHrefs: string[] }>;
export type SetCalendarKeysPayload = PayloadAction<{ calendarKeys: string[] }>;
export type LoadGoalsForPlansPayload = PayloadAction<{ plans: Array<{ key: string }> }>;
export type LoadTranslationsPayload = PayloadAction<{
  selectedCurriculum: CurriculumParams[];
  originalCurriculum?: CurriculumParams[];
}>;
export type SaveActivityPayload = PayloadAction<{ isCreate: boolean; [key: string]: unknown }>;
export type DuplicateActivityPayload = PayloadAction<DuplicateActivityModalData>;
export type ConfirmDuplicateActivityPayload = PayloadAction<
  DuplicateActivityModalData & { startDate: string }
>;
